// src/features/ACS-Dashboard-Modules/Appointments/TabContent/Calendar-Main/DockDoorSettings.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { format, parseISO } from "date-fns";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { FaRegCalendarAlt } from "react-icons/fa";
import httpClient from "../../../../../api/httpClient";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { removeTab, setActiveTab } from "../../../../../redux/slices/appointmentsSlices";
import notify from "../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import DayOfWeekMultiSelect from "./DayOfWeekMultiSelect";
import {
  buildRecurringEventPayload,
  hasIncompleteEndDate,
  isUnsavedRecurringEvent,
  validateNoOverlappingRecurringEvents,
} from "../utils/recurringEventUtils";

const RECURRENCE_PATTERNS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "YEARLY", label: "Yearly" },
];

const INTERVAL_UNIT_LABELS = {
  DAILY: "day(s)",
  WEEKLY: "week(s)",
  MONTHLY: "month(s)",
  YEARLY: "year(s)",
};

const WEEK_OF_MONTH_OPTIONS = [
  { value: 1, label: "First" },
  { value: 2, label: "Second" },
  { value: 3, label: "Third" },
  { value: 4, label: "Fourth" },
  { value: -1, label: "Last" },
];

const buildErrorsMapForRecurringEvents = (events) => {
  const { conflicts } = validateNoOverlappingRecurringEvents(events);
  const errors = {};
  conflicts.forEach(({ indexA, indexB, date }) => {
    const nameA = events[indexA]?.eventName || "another event";
    const nameB = events[indexB]?.eventName || "another event";
    errors[indexA] = `Overlaps with "${nameB}" on ${date}`;
    errors[indexB] = `Overlaps with "${nameA}" on ${date}`;
  });
  // Takes precedence: an unfinished field is more actionable than an overlap that
  // may well disappear once the end date is filled in.
  events.forEach((re, idx) => {
    if (hasIncompleteEndDate(re)) errors[idx] = "Pick an end date, or choose Indefinitely";
  });
  return errors;
};

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DockDoorSettings = ({
  settings = {},
  onChange,
  isViewOnly = false,
  isWarehouseView = false,
  calendarRefId = null,
  isNewDock = false,
  registerHandleSave,
  onSaveComplete,
  showInlineSave = false,
  inlineSaveLabel = "Save",
  inlineSaveDisabled = false,
  onInlineSave,
  onSettingsSavingChange = () => {},
  disableTabRedirect = false,
  // Lets a view-only panel (the Settings warehouse panel) add recurring events without
  // unlocking anything else: saved rows stay read-only, and the new rows are handed to
  // onApplyRecurringDrafts instead of going through handleSave.
  canAddRecurringDrafts = false,
  onApplyRecurringDrafts,
  recurringDraftsBusy = false,
  recurringDraftsApplyLabel = "Apply",
  recurringDraftsHint = "",
}) => {
  const [regularHours, setRegularHours] = useState(settings.regularHours || {});
  const [holidays, setHolidays] = useState(settings.holidays || []);
  const [blackouts, setBlackouts] = useState(settings.blackouts || []);
  const [recurringEvents, setRecurringEvents] = useState(settings.recurringEvents || []);
  const [recurringEventErrors, setRecurringEventErrors] = useState({});
  const [deletingRecurringEventId, setDeletingRecurringEventId] = useState(null);
  const [isApplyingRecurringDrafts, setIsApplyingRecurringDrafts] = useState(false);

  const [appointmentDurationHours, setAppointmentDurationHours] = useState(0);
  const [holidayCalendarMonth, setHolidayCalendarMonth] = useState(new Date());
  const [blackoutCalendarMonth, setBlackoutCalendarMonth] = useState(new Date());
  const [openHolidayMonths, setOpenHolidayMonths] = useState({});
  const [openBlackoutMonths, setOpenBlackoutMonths] = useState({});
  const [showHolidayPicker, setShowHolidayPicker] = useState(false);
  const [showBlackoutPicker, setShowBlackoutPicker] = useState(false);
  const holidayPickerRef = useRef(null);
  const blackoutPickerRef = useRef(null);
  const dispatch = useDispatch();
  const { activeTab } = useSelector(
    (state) => state.appointments
  );
  const [deletingHolidayId, setDeletingHolidayId] = useState(null);
  const [deletingBlackoutId, setDeletingBlackoutId] = useState(null);
  const [isInlineSaving, setIsInlineSaving] = useState(false);
  const BLACKOUT_REGEX = /\bblackout\b/i;

  useEffect(() => {
    setRegularHours(settings.regularHours || {});
    setHolidays(settings.holidays || []);
    setBlackouts(settings.blackouts || []);
    setRecurringEvents(settings.recurringEvents || []);


    if (
      typeof settings.appointmentDuration === "number" &&
      settings.appointmentDuration > 0
    ) {
      const hours = settings.appointmentDuration / 60;
      setAppointmentDurationHours(hours);
    } else {
      setAppointmentDurationHours(2);
    }
  }, [settings]);

  const isInputDisabled = isViewOnly;

  // In a view-only panel only unsaved rows can be edited, and only while canAddRecurringDrafts allows it.
  const isDraftOnlyMode = isInputDisabled && canAddRecurringDrafts;
  // The parent's flag survives this panel remounting mid-request; the local one covers the
  // validation step before the parent takes over.
  const draftsBusy = isApplyingRecurringDrafts || recurringDraftsBusy;
  const canEditRecurringEvent = (re) =>
    !isInputDisabled || (isDraftOnlyMode && !draftsBusy && isUnsavedRecurringEvent(re));

  const handleHoursChange = (day, key, value) => {
    if (isInputDisabled) return;
    const updated = {
      ...regularHours,
      [day]: {
        ...regularHours[day],
        [key]: value,
      },
    };
    setRegularHours(updated);
    onChange({ ...settings, regularHours: updated, holidays, blackouts });
  };

  const addHoliday = () => {
    if (isInputDisabled) return;
    const newHoliday = {
      date: format(new Date(), "yyyy-MM-dd"),
      allDay: true,
      reason: "",
    };
    const updated = [...holidays, newHoliday];
    setHolidays(updated);
  };

  const removeHoliday = async (idx) => {
    const holiday = holidays[idx];
    if (!holiday) return;

    const hasEventId =
      !isNewDock &&
      holiday.eventId !== undefined &&
      holiday.eventId !== null;

    if (hasEventId) {
      setDeletingHolidayId(holiday.eventId);
      try {
        await httpClient.delete(
          `/api/events/${holiday.eventId}`
        );
      } catch (error) {
        console.error("❌ Failed to delete holiday event:", error);
        notify.error({
          title: "Delete failed",
          message: "Could not delete holiday. Please try again.",
        });
        setDeletingHolidayId(null);
        return;
      }
    }

    const updated = holidays.filter((_, i) => i !== idx);
    setHolidays(updated);
    onChange({
      ...settings,
      regularHours,
      holidays: updated,
      blackouts,
    });
    setDeletingHolidayId(null);
  };

  const addBlackout = () => {
    if (isInputDisabled) return;
    const newBlackout = {
      from: format(new Date(), "yyyy-MM-dd"),
      to: format(new Date(), "yyyy-MM-dd"),
      allDay: true,
      reason: "Blackout",
    };
    const updated = [...blackouts, newBlackout];
    setBlackouts(updated);
    onChange({ ...settings, regularHours, holidays, blackouts: updated });
  };

  const removeBlackout = async (idx) => {
    const blackout = blackouts[idx];
    if (!blackout) return;

    const hasExceptionId =
      !isNewDock &&
      blackout.exceptionId !== undefined &&
      blackout.exceptionId !== null;

    if (hasExceptionId) {
      setDeletingBlackoutId(blackout.exceptionId);
      try {
        await httpClient.delete(
          `/api/exception-events/${blackout.exceptionId}`
        );
      } catch (error) {
        console.error("❌ Failed to delete blackout:", error);
        notify.error({
          title: "Delete failed",
          message: "Could not delete blackout day. Please try again.",
        });
        setDeletingBlackoutId(null);
        return;
      }
    }

    const updated = blackouts.filter((_, i) => i !== idx);
    setBlackouts(updated);
    onChange({
      ...settings,
      regularHours,
      holidays,
      blackouts: updated,
    });
    setDeletingBlackoutId(null);
  };

  const addRecurringEvent = () => {
    if (isInputDisabled && (!isDraftOnlyMode || draftsBusy)) return;
    const newEvent = {
      eventName: "",
      allDay: false,
      startTime: "09:00",
      endTime: "10:00",
      recurrencePattern: "DAILY",
      intervalCount: 1,
      daysOfWeek: [],
      monthlyMode: "DAY_OF_MONTH",
      weekOfMonth: 1,
      monthlyDayOfWeek: "MONDAY",
      dayOfMonth: 1,
      monthOfYear: 1,
      effectiveDate: format(new Date(), "yyyy-MM-dd"),
      endMode: "INDEFINITELY",
      endDate: null,
    };
    const updated = [...recurringEvents, newEvent];
    setRecurringEvents(updated);
    setRecurringEventErrors(buildErrorsMapForRecurringEvents(updated));
    onChange({ ...settings, regularHours, holidays, blackouts, recurringEvents: updated });
  };

  const updateRecurringEventField = (idx, field, value) => {
    if (!canEditRecurringEvent(recurringEvents[idx])) return;
    const updated = [...recurringEvents];
    const current = { ...updated[idx], [field]: value };

    // Forbid "All Day" for Yearly recurring events — that shape is
    // indistinguishable from a Holiday on read-back, and Holidays already
    // cover whole-day, once-a-year closures.
    if (field === "recurrencePattern" && value === "YEARLY") {
      current.allDay = false;
    }

    // Switching to Indefinitely drops any date already picked, so a stale value
    // can't linger out of sight and get sent on the next save.
    if (field === "endMode" && value === "INDEFINITELY") {
      current.endDate = null;
    }

    updated[idx] = current;
    setRecurringEvents(updated);
    setRecurringEventErrors(buildErrorsMapForRecurringEvents(updated));
    onChange({ ...settings, regularHours, holidays, blackouts, recurringEvents: updated });
  };

  const removeRecurringEvent = async (idx) => {
    const entry = recurringEvents[idx];
    if (!entry || !canEditRecurringEvent(entry)) return;

    const hasEventId =
      !isNewDock && entry.recurringEventId !== undefined && entry.recurringEventId !== null;

    if (hasEventId) {
      setDeletingRecurringEventId(entry.recurringEventId);
      try {
        await httpClient.delete(
          `/api/events/${entry.recurringEventId}`
        );
      } catch (error) {
        console.error("❌ Failed to delete recurring event:", error);
        notify.error({
          title: "Delete failed",
          message: "Could not delete recurring event. Please try again.",
        });
        setDeletingRecurringEventId(null);
        return;
      }
    }

    const updated = recurringEvents.filter((_, i) => i !== idx);
    setRecurringEvents(updated);
    setRecurringEventErrors(buildErrorsMapForRecurringEvents(updated));
    onChange({ ...settings, regularHours, holidays, blackouts, recurringEvents: updated });
    setDeletingRecurringEventId(null);
  };

  const recurringDrafts = recurringEvents.filter(isUnsavedRecurringEvent);
  const showRecurringDraftActions = isDraftOnlyMode && recurringDrafts.length > 0;

  const discardRecurringDrafts = () => {
    const updated = recurringEvents.filter((re) => !isUnsavedRecurringEvent(re));
    setRecurringEvents(updated);
    setRecurringEventErrors(buildErrorsMapForRecurringEvents(updated));
    onChange({ ...settings, regularHours, holidays, blackouts, recurringEvents: updated });
  };

  const applyRecurringDrafts = async () => {
    if (draftsBusy || !onApplyRecurringDrafts || !recurringDrafts.length) return;

    // Recomputed rather than read from state: the panel remounts when the user
    // switches between docks, which resets the error map but keeps the drafts.
    const errors = buildErrorsMapForRecurringEvents(recurringEvents);
    setRecurringEventErrors(errors);
    if (recurringEvents.some((re, idx) => isUnsavedRecurringEvent(re) && errors[idx])) {
      notify.error({
        title: "Fix recurring events",
        message: "Resolve the issues shown on the new recurring events before applying.",
      });
      return;
    }

    setIsApplyingRecurringDrafts(true);
    try {
      await onApplyRecurringDrafts(recurringDrafts);
    } finally {
      setIsApplyingRecurringDrafts(false);
    }
  };

  const addHolidayDates = (dates = []) => {
    if (isInputDisabled || !dates.length) return;
    const existing = new Set(holidays.map((h) => h.date));
    const additions = [];

    dates.forEach((day) => {
      const dateStr = format(day, "yyyy-MM-dd");
      if (!existing.has(dateStr)) {
        existing.add(dateStr);
        additions.push({ date: dateStr, allDay: true, reason: "" });
      }
    });

    if (additions.length) {
      const updated = [...holidays, ...additions].sort((a, b) =>
        a.date.localeCompare(b.date)
      );
      setHolidays(updated);
      onChange({ ...settings, regularHours, holidays: updated, blackouts });
    }
  };

  const addBlackoutDate = (day) => {
    if (isInputDisabled || !day) return;
    const fromStr = format(day, "yyyy-MM-dd");
    const toStr = fromStr;
    const isDuplicate = blackouts.some((b) => b.from === fromStr && b.to === toStr);
    if (isDuplicate) return;

    const updated = [...blackouts, { from: fromStr, to: toStr, allDay: true, reason: "Blackout" }].sort(
      (a, b) => a.from.localeCompare(b.from)
    );
    setBlackouts(updated);
    onChange({ ...settings, regularHours, holidays, blackouts: updated });
  };

  const groupedHolidays = useMemo(() => {
    const buckets = {};
    holidays.forEach((h) => {
      if (!h?.date) return;
      const dateObj = parseISO(h.date);
      const key = format(dateObj, "yyyy-MM");
      if (!buckets[key]) {
        buckets[key] = { label: format(dateObj, "MMMM yyyy"), items: [] };
      }
      buckets[key].items.push(h);
    });
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({
        key,
        ...value,
        items: value.items.sort((a, b) => a.date.localeCompare(b.date)),
      }));
  }, [holidays]);

  const groupedBlackouts = useMemo(() => {
    const buckets = {};
    blackouts
      .filter((b) => BLACKOUT_REGEX.test(b.reason))
      .forEach((b) => {
        if (!b?.from) return;
        const dateObj = parseISO(b.from);
        const key = format(dateObj, "yyyy-MM");
        if (!buckets[key]) {
          buckets[key] = { label: format(dateObj, "MMMM yyyy"), items: [] };
        }
        buckets[key].items.push(b);
      });
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({
        key,
        ...value,
        items: value.items.sort((a, b) => a.from.localeCompare(b.from)),
      }));
  }, [blackouts]);

  useEffect(() => {
    setOpenHolidayMonths((prev) => {
      const next = { ...prev };
      groupedHolidays.forEach(({ key }) => {
        if (next[key] === undefined) next[key] = true;
      });
      return next;
    });
  }, [groupedHolidays]);

  useEffect(() => {
    setOpenBlackoutMonths((prev) => {
      const next = { ...prev };
      // key: day, value: obj
      groupedBlackouts.forEach(({ key }) => {
        if (next[key] === undefined) next[key] = true;
      });
      return next;
    });
  }, [groupedBlackouts]);

  useEffect(() => {
    const handler = (e) => {
      if (
        holidayPickerRef.current &&
        !holidayPickerRef.current.contains(e.target) &&
        showHolidayPicker
      ) {
        setShowHolidayPicker(false);
      }
      if (
        blackoutPickerRef.current &&
        !blackoutPickerRef.current.contains(e.target) &&
        showBlackoutPicker
      ) {
        setShowBlackoutPicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showHolidayPicker, showBlackoutPicker]);

  const handleSave = useCallback(async () => {
    onSettingsSavingChange(true);

    // "Ends on date" with no date would otherwise be saved as null, i.e. silently
    // turn into the indefinite recurrence the user just opted out of.
    if (recurringEvents.some(hasIncompleteEndDate)) {
      notify.error({
        title: "Missing end date",
        message:
          'Pick an end date for each recurring event set to "On date", or choose "Indefinitely".',
      });
      onSettingsSavingChange(false);
      return false;
    }

    // Defensive re-check: a conflict must never reach the API, even if a
    // race in row-editing slipped past the inline per-row validation.
    const { valid: recurringEventsValid } = validateNoOverlappingRecurringEvents(recurringEvents);
    if (!recurringEventsValid) {
      notify.error({
        title: "Overlapping recurring events",
        message: "Resolve overlapping recurring events before saving.",
      });
      onSettingsSavingChange(false);
      return false;
    }

    onChange({
      ...settings,
      regularHours,
      holidays,
      blackouts,
      recurringEvents,

    });

    const regularHourUpdates = Object.values(regularHours)
      .filter((entry) => entry.eventId && entry.from && entry.to)
      .map((entry) => ({
        eventId: entry.eventId,
        startTime: entry.from + ":00",
        endTime: entry.to + ":00",
      }));

    const regularHourDeletes = Object.values(regularHours)
      .filter((entry) => entry.eventId && (!entry.from || !entry.to))
      .map((entry) => entry.eventId);

    const holidayUpdates = holidays
      .filter((h) => h.eventId !== undefined)
      .map((h) => {
        const [year, month, day] = h.date.split("-").map(Number);
        return {
          eventId: h.eventId,
          eventDescription: h.reason || "Holiday",
          dayOfMonth: day,
          monthOfYear: month,
        };
      });

    const newHolidayEvents = holidays
      .filter((h) => !h.eventId)
      .map((h) => {
        const [year, month, day] = h.date.split("-").map(Number);
        return {
          eventDescription: h.reason || "Holiday",
          eventStatueType: "CLOSE",
          eventRecurringType: "YEARLY",
          startDate: h.date,
          isCancelled: false,
          isWholeDay: true,
          dayOfMonth: day,
          monthOfYear: month,
          intervalCount: 1,
          calendarId: settings.calendarId,
        };
      });

    const blackoutPayload = blackouts
      .filter((b) => b.exceptionId !== undefined && b.exceptionId !== null)
      .map((b) => ({
        exceptionId: b.exceptionId,
        startDate: b.from,
        endDate: b.to,
        reason: b.reason,
      }));

    const newBlackouts = blackouts
      .filter((b) => !b.exceptionId)
      .map((b) => ({
        calendarId: settings.calendarId,
        startDate: b.from,
        endDate: b.to,
        isWholeDay: true,
        overrideType: "CLOSE",
        reason: b.reason || "Blackout",
      }));

    const newRegularHourEvents = Object.entries(regularHours)
      .filter(
        ([, entry]) =>
          !entry.eventId &&
          entry.from &&
          entry.to
      )
      .map(([day, entry]) => ({
        eventDescription: "Open",
        eventStatueType: "OPEN",
        eventRecurringType: "WEEKLY",
        startDate: new Date().toISOString().split("T")[0],
        isCancelled: false,
        isWholeDay: false,
        dayOfWeek: day.toUpperCase(),
        startTime: `${entry.from}:00`,
        endTime: `${entry.to}:00`,
        intervalCount: 1,
        calendarId: settings.calendarId,
      }));

    const recurringEventUpdates = recurringEvents
      .filter((re) => re.recurringEventId !== undefined)
      .map((re) => buildRecurringEventPayload(re, settings.calendarId));

    const newRecurringEvents = recurringEvents
      .filter((re) => re.recurringEventId === undefined)
      .map((re) => buildRecurringEventPayload(re, settings.calendarId));

    const Eventpayload = [...regularHourUpdates, ...holidayUpdates, ...recurringEventUpdates];
    const newEvents = [...newHolidayEvents, ...newRegularHourEvents, ...newRecurringEvents];

    const promises = [];

    const durationValue = settings.appointmentDuration;
    if (durationValue) {
      promises.push(
        httpClient.put(
          `/api/calendar/update/by-ref/${settings.calendarRefId}`,
          { appointmentDuration: durationValue }
        )
      );
    }

    promises.push(
      httpClient.patch(
        `/api/events/events/bulk-update`,
        Eventpayload
      )
    );

    // Delete any weekly open events cleared out by the user
    regularHourDeletes.forEach((eventId) => {
      promises.push(
        httpClient.delete(`/api/events/${eventId}`)
      );
    });

    promises.push(
      httpClient.patch(
        `/api/exception-events/bulk-update`,
        blackoutPayload
      )
    );

    promises.push(
      httpClient.post(
        `/api/events/events`,
        newEvents
      )
    );

    promises.push(
      httpClient.post(
        `/api/exception-events/exception-events`,
        newBlackouts
      )
    );

    try {
      const results = await Promise.allSettled(promises);
      let allSuccess = true;
      results.forEach((result, idx) => {
        if (result.status === "fulfilled") {
        } else {
          allSuccess = false;
          console.error(`❌ API ${idx + 1} failed:`, result.reason);
        }
      });

      if (allSuccess) {
        if (!disableTabRedirect) {
          dispatch(removeTab({ tabIndex: activeTab }));
          dispatch(setActiveTab({ index: 0 }));
        }
        if (onSaveComplete) {
          onSaveComplete();
        }
      } else {
        console.warn("⚠️ Not all API calls succeeded. Page will not reload.");
      }
      return allSuccess;
    } catch (err) {
      console.error("Unexpected error in batch API handling:", err);
      return false;
    } finally {
      onSettingsSavingChange(false);
    }
  }, [
    regularHours,
    holidays,
    blackouts,
    recurringEvents,

    settings,
    onChange,
    onSettingsSavingChange,
    dispatch,
    activeTab,
    onSaveComplete,
    disableTabRedirect,
  ]);

  useEffect(() => {
    if (registerHandleSave) {
      registerHandleSave(handleSave);
    }
  }, [registerHandleSave, handleSave]);

  const handleInlineSave = async () => {
    if (isInlineSaving || inlineSaveDisabled) return;
    setIsInlineSaving(true);
    const success = await handleSave();
    if (success && onInlineSave) {
      await onInlineSave();
    }
    setIsInlineSaving(false);
  };

  return (
    <section className="mt-fluid-1">
    {isViewOnly && !isWarehouseView && (
    <div className="mb-fluid-2 flex justify-between items-center">
      {/* Left side — Heading */}
      <h3 className="text-fluid-lg font-semibold">Regular Hours</h3>

      {/* Right side — Edit toggle + Save */}
      <div className="flex items-center gap-3">
        {/* <label className="text-fluid-base font-medium">Edit dock</label> */}

        {/* Toggle */}
        {/* <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={isEditing}
            onChange={toggleEditing}
          />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
          <div className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-full"></div>
        </label> */}

        {/* Save button (only if editing) */}
       {/* {isEditing && !isNewDock && (
          <button
            onClick={handleSave}
            className="bg-[#223688] text-white text-fluid-sm font-semibold px-4 py-2 rounded-md hover:bg-[#1A2B6D] transition"
          >
            Save
          </button>
        )} */}

      </div>
    </div>
  )}

  {!isViewOnly && showInlineSave && (
    <div className="mb-fluid-2 flex justify-between items-center">
      <h3 className="text-fluid-lg font-semibold">Regular hours</h3>
      <button
        onClick={handleInlineSave}
        className="bg-[#223688] text-white text-fluid-sm font-semibold px-4 py-2 rounded-md hover:bg-[#1A2B6D] transition disabled:opacity-60 disabled:cursor-not-allowed"
        disabled={isInlineSaving || inlineSaveDisabled}
      >
        {isInlineSaving ? "Saving..." : inlineSaveLabel}
      </button>
    </div>
  )}


{/* 4-column layout */}
<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-fluid-4 items-start">

  {/* Col 1 — Weekly Hours */}
  <div className="min-w-[300px]">
    <div className="flex flex-col gap-fluid-2">
      {days.map((day) => (
        <div key={day} className="flex gap-fluid-1 items-center">
          <label className="w-20 text-fluid-sm font-semibold">{day}</label>
          <input
            className="w-[clamp(10rem,12vw,8rem)] px-1 py-1 border border-gray-300 rounded"
            type="time"
            value={regularHours[day]?.from || ""}
            onChange={(e) => handleHoursChange(day, "from", e.target.value)}
            disabled={isInputDisabled}
          />
          <input
            className="w-[clamp(10rem,12vw,8rem)] px-2 py-1 border border-gray-300 rounded"
            type="time"
            value={regularHours[day]?.to || ""}
            onChange={(e) => handleHoursChange(day, "to", e.target.value)}
            disabled={isInputDisabled}
          />
        </div>
      ))}
    </div>

    {/* Appointment Duration (stacked below hours) */}
    <div className="mt-fluid-4">
      <h3 className="text-fluid-lg font-semibold mb-fluid-2">
        Appointment Duration
      </h3>

      <div className="flex flex-col gap-fluid-1">
        <label className="text-fluid-sm font-medium">
          Duration (hours)
        </label>
        <input
          type="number"
          min="0.25"
          step="0.25"
          inputMode="decimal"
          value={appointmentDurationHours}
          onChange={(e) => {
            const value = e.target.value;
            const hours = parseFloat(value);
            
            setAppointmentDurationHours(hours || 0);
            
            if (isNaN(hours) || hours < 0.25) return;
            
            const snappedHours = Math.round(hours * 4) / 4;
            const minutes = Math.round(snappedHours * 60);
            
            onChange({
              ...settings,
              regularHours,
              holidays,
              blackouts,
              appointmentDuration: Number(minutes),
            });
          }}
          onBlur={(e) => {
            const hours = parseFloat(e.target.value);
            if (!isNaN(hours) && hours >= 0.25) {
              const snappedHours = Math.round(hours * 4) / 4;
              setAppointmentDurationHours(snappedHours);
            } else {
              setAppointmentDurationHours(0.25);
            }
          }}
          disabled={isInputDisabled}
          className="border border-gray-300 rounded px-2 py-1 text-fluid-sm w-[10rem]"
          placeholder="e.g. 1.25"
        />
      </div>
    </div>
  </div>


  {/* Col 2 — Holidays */}
  <div className="min-w-[250px] min-h-0">
    <div className="flex items-center gap-fluid-2 mb-fluid-2 relative">
      <h3 className="text-fluid-md font-semibold">
        Holidays
      </h3>

      {(!isViewOnly) && (
        <div className="flex items-center gap-fluid-1">
          <button
            onClick={() => setShowHolidayPicker((p) => !p)}
            className="
              p-fluid-1 
              rounded-md 
              bg-custom-bg-light-purple 
              text-acs-primary-blue 
              border border-acs-primary-blue/30
              flex items-center justify-center
              shadow-sm
            "
            aria-label="Add holiday"
          >
            <Plus className="w-fluid-icon-xs h-fluid-icon-xs" strokeWidth={2.3} />
          </button>
          <span className="text-fluid-xs text-gray-500">Pick date</span>
        </div>
      )}

      {showHolidayPicker && !isViewOnly && (
        <div
          ref={holidayPickerRef}
          className="absolute top-full left-0 mt-fluid-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-fluid-2 min-w-[320px] max-w-[360px] max-h-[60vh] overflow-y-auto"
        >
          <div className="flex justify-between items-center mb-fluid-1">
            <span className="text-fluid-sm font-semibold text-gray-800">Add holidays</span>
            <button
              type="button"
              className="text-fluid-xs text-gray-500 hover:text-gray-800"
              onClick={() => setShowHolidayPicker(false)}
            >
              Close
            </button>
          </div>
          <DayPicker
            mode="single"
            weekStartsOn={1}
            onDayClick={(day) => {
              addHolidayDates([day]);
            }}
            month={holidayCalendarMonth}
            onMonthChange={setHolidayCalendarMonth}
            showOutsideDays
            modifiers={{
              existing: holidays.map((h) => parseISO(h.date)),
            }}
            modifiersClassNames={{
              existing: "bg-[#1e2a74] text-white",
            }}
            className="text-gray-800 w-full"
          />
        </div>
      )}
    </div>

    <div className="mt-fluid-2 flex flex-col gap-fluid-2 max-h-[50vh] overflow-y-auto overflow-x-hidden pr-fluid-1">
      {groupedHolidays.map((group) => (
        <div key={group.key} className="border border-gray-200 rounded-lg">
          {(() => {
            const isOpen = openHolidayMonths[group.key] ?? true;
            return (
              <button
                type="button"
                onClick={() =>
                  setOpenHolidayMonths((prev) => ({
                    ...prev,
                    [group.key]: !isOpen,
                  }))
                }
                className="w-full flex items-center justify-between px-fluid-2 py-fluid-2 bg-gray-50 text-left"
              >
                <span className="text-fluid-sm font-semibold text-gray-800 flex items-center gap-2">
                  {group.label}
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </span>
                <span className="text-fluid-xs text-gray-500">
                  {group.items.length} day{group.items.length > 1 ? "s" : ""}
                </span>
              </button>
            );
          })()}
          {(openHolidayMonths[group.key] ?? true) && (
            <div className="p-fluid-2 flex flex-col gap-fluid-2 max-h-[18rem] overflow-y-auto overflow-x-hidden">
              <div className="text-xs text-gray-500">
                Checkbox indicates this holiday is marked as all-day.
              </div>
              {group.items.map((h, idx) => {
                const useIndex = holidays.indexOf(h) >= 0 ? holidays.indexOf(h) : idx;
                return (
                  <div key={`${group.key}-${idx}`} className="flex items-center gap-fluid-2">
                    <div className="relative flex items-center">
                      <FaRegCalendarAlt className="absolute left-2 text-[#1e2a74] w-4 h-4 pointer-events-none" />
                      <input
                        type="date"
                        value={h.date}
                        onChange={(e) => {
                          if (isInputDisabled) return;
                          const updated = [...holidays];
                          updated[useIndex].date = e.target.value;
                          setHolidays(updated);
                          onChange({ ...settings, regularHours, holidays: updated, blackouts, appointmentDurationHours });
                        }}
                        disabled={isInputDisabled}
                        className="pl-7 pr-2 py-1 border border-gray-300 rounded bg-white text-fluid-sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={h.reason || ""}
                      placeholder="Reason"
                      onChange={(e) => {
                        if (isInputDisabled) return;
                        const updated = [...holidays];
                        updated[useIndex].reason = e.target.value;
                        setHolidays(updated);
                        onChange({ ...settings, regularHours, holidays: updated, blackouts, appointmentDurationHours });
                      }}
                      disabled={isInputDisabled}
                      className="border border-gray-300 rounded px-2 py-1 text-fluid-sm"
                      style={{ minWidth: "10rem" }}
                    />
                    <label className="inline-flex items-center gap-1 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={h.allDay}
                        onChange={(e) => {
                          if (isInputDisabled) return;
                          const updated = [...holidays];
                          updated[useIndex].allDay = e.target.checked;
                          setHolidays(updated);
                          onChange({ ...settings, regularHours, holidays: updated, blackouts, appointmentDurationHours });
                        }}
                        disabled={isInputDisabled}
                        aria-label="Mark holiday as all-day"
                      />
                    </label>
                    {!isInputDisabled && (
                      <button
                        onClick={() => removeHoliday(useIndex)}
                        disabled={deletingHolidayId === h.eventId}
                        className="
                          p-fluid-1 
                          rounded-md 
                          text-Delete-red 
                          flex items-center justify-center
                        "
                      >
                        <Trash2
                          className="w-fluid-icon-xs h-fluid-icon-xs"
                          strokeWidth={2.3}
                        />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>

  {/* Col 3 — Blackouts */}
  <div className="min-w-[250px] min-h-0">
    <div className="flex items-center gap-fluid-2 mb-fluid-2 relative">
      <h3 className="text-fluid-lg font-semibold">
        Blackout Days
      </h3>

      {/* Inline Plus Button */}
      {(!isViewOnly) && (
        <div className="flex items-center gap-fluid-1">
          <button
            onClick={() => setShowBlackoutPicker((p) => !p)}
            className="
              p-fluid-1 
              rounded-md 
              bg-custom-bg-light-purple 
              text-acs-primary-blue 
              border border-acs-primary-blue/30
              flex items-center justify-center
              shadow-sm
            "
            aria-label="Add blackout day"
          >
            <Plus className="w-fluid-icon-xs h-fluid-icon-xs" strokeWidth={2.3} />
          </button>
          <span className="text-fluid-xs text-gray-500">Pick date</span>
        </div>
      )}

      {showBlackoutPicker && !isViewOnly && (
        <div
          ref={blackoutPickerRef}
          className="absolute top-full left-0 mt-fluid-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-fluid-2 min-w-[320px] max-w-[360px] max-h-[60vh] overflow-y-auto"
        >
          <div className="flex justify-between items-center mb-fluid-1">
            <span className="text-fluid-sm font-semibold text-gray-800">Add blackout</span>
            <button
              type="button"
              className="text-fluid-xs text-gray-500 hover:text-gray-800"
              onClick={() => setShowBlackoutPicker(false)}
            >
              Close
            </button>
          </div>
          <DayPicker
            mode="single"
            weekStartsOn={1}
            onDayClick={(day) => {
              addBlackoutDate(day);
            }}
            month={blackoutCalendarMonth}
            onMonthChange={setBlackoutCalendarMonth}
            showOutsideDays
            modifiers={{
              existing: blackouts.map((b) => ({
                from: parseISO(b.from),
                to: b.to ? parseISO(b.to) : parseISO(b.from),
              })),
            }}
            modifiersClassNames={{
              existing: "bg-[#1e2a74] text-white",
            }}
            className="text-gray-800 w-full"
          />
        </div>
      )}
    </div>
    <div className="mt-fluid-2 flex flex-col gap-fluid-2 max-h-[50vh] overflow-y-auto overflow-x-hidden pr-fluid-1">
      {groupedBlackouts.map((group) => ( group.items.length === 0 ? null : (
        <div key={group.key} className="border border-gray-200 rounded-lg">
          {(() => {
            const isOpen = openBlackoutMonths[group.key] ?? true;
            return (
              <button
                type="button"
                onClick={() =>
                  setOpenBlackoutMonths((prev) => ({
                    ...prev,
                    [group.key]: !isOpen,
                  }))
                }
                className="w-full flex items-center justify-between px-fluid-2 py-fluid-2 bg-gray-50 text-left"
              >
                <span className="text-fluid-sm font-semibold text-gray-800 flex items-center gap-2">
                  {group.label}
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </span>
                <span className="text-fluid-xs text-gray-500">
                  {group.items.length} {group.items.length > 1 ? "entries" : "entry"}
                </span>
              </button>
            );
          })()}
          {(openBlackoutMonths[group.key] ?? true) && (
            <div className="p-fluid-2 flex flex-col gap-fluid-2 max-h-[18rem] overflow-y-auto overflow-x-hidden">
              <div className="text-xs text-gray-500">
                Checkbox indicates this blackout is marked as all-day.
              </div>
              {group.items.map((b, idx) => {
                const useIndex = blackouts.indexOf(b) >= 0 ? blackouts.indexOf(b) : idx;
                return (
                  <div key={`${group.key}-${idx}`} className="flex items-center gap-fluid-2">
                    <div className="relative flex items-center">
                      <FaRegCalendarAlt className="absolute left-2 text-[#1e2a74] w-4 h-4 pointer-events-none" />
                      <input
                        type="date"
                        value={b.from}
                        onChange={(e) => {
                          if (isInputDisabled) return;
                          const updated = [...blackouts];
                          updated[useIndex].from = e.target.value;
                          setBlackouts(updated);
                          onChange({ ...settings, regularHours, holidays, blackouts: updated, appointmentDurationHours });
                        }}
                        disabled={isInputDisabled}
                        className="pl-7 pr-2 py-1 border border-gray-300 rounded bg-white text-fluid-sm"
                      />
                    </div>
                    <div className="relative flex items-center">
                      <FaRegCalendarAlt className="absolute left-2 text-[#1e2a74] w-4 h-4 pointer-events-none" />
                      <input
                        type="date"
                        value={b.to}
                        onChange={(e) => {
                          if (isInputDisabled) return;
                          const updated = [...blackouts];
                          updated[useIndex].to = e.target.value;
                          setBlackouts(updated);
                          onChange({ ...settings, regularHours, holidays, blackouts: updated, appointmentDurationHours });
                        }}
                        disabled={isInputDisabled}
                        className="pl-7 pr-2 py-1 border border-gray-300 rounded bg-white text-fluid-sm"
                      />
                    </div>
                    <label className="inline-flex items-center gap-1 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={b.allDay}
                        onChange={(e) => {
                          if (isInputDisabled) return;
                          const updated = [...blackouts];
                          updated[useIndex].allDay = e.target.checked;
                          setBlackouts(updated);
                          onChange({ ...settings, regularHours, holidays, blackouts: updated, appointmentDurationHours });
                        }}
                        disabled={isInputDisabled}
                        aria-label="Mark blackout as all-day"
                      />
                    </label>
                    {!isInputDisabled && (
                      <button
                        onClick={() => removeBlackout(useIndex)}
                        disabled={
                          deletingBlackoutId !== null &&
                          blackouts[useIndex]?.exceptionId === deletingBlackoutId
                        }
                        className="
                          p-fluid-1 
                          rounded-md 
                          text-Delete-red 
                          flex items-center justify-center
                          disabled:opacity-60 disabled:cursor-not-allowed
                        "
                      >
                        <Trash2 className="w-fluid-icon-xs h-fluid-icon-xs" strokeWidth={2.3} />
                      </button>
                    )}

                    {/* <input
                      type="text"
                      value={b.reason || ""}
                      placeholder="Reason"
                      onChange={(e) => {
                        if (isInputDisabled) return;
                        const updated = [...blackouts];
                        updated[useIndex].reason = e.target.value;
                        setBlackouts(updated);
                        onChange({ ...settings, regularHours, holidays, blackouts: updated, appointmentDuration });
                      }}
                      disabled={isInputDisabled}
                      className="border border-gray-300 rounded px-2 py-1 text-fluid-sm"
                      style={{ minWidth: "10rem" }}
                    /> */}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )))}
    </div>
  </div>
</div>

{/*
  Recurring Events (full-width — each entry needs more fields than fit in a grid
  column). Shown at both dock and warehouse level. In the view-only Settings
  warehouse panel, canAddRecurringDrafts lets the user add new rows. Those rows go
  to onApplyRecurringDrafts, which saves them to the warehouse and every dock.
*/}
<div className="mt-fluid-4 w-full">
  <div className="flex items-center gap-fluid-2 mb-fluid-2">
    <h3 className="text-fluid-lg font-semibold">
      Recurring Events
    </h3>
    {(!isViewOnly || canAddRecurringDrafts) && (
      <button
        onClick={addRecurringEvent}
        disabled={draftsBusy}
        className="
          p-fluid-1
          rounded-md
          bg-custom-bg-light-purple
          text-acs-primary-blue
          border border-acs-primary-blue/30
          flex items-center justify-center
          shadow-sm
          disabled:opacity-60 disabled:cursor-not-allowed
        "
        aria-label="Add recurring event"
      >
        <Plus className="w-fluid-icon-xs h-fluid-icon-xs" strokeWidth={2.3} />
      </button>
    )}
  </div>

  <div className="flex flex-col gap-fluid-2 overflow-x-auto">
    {recurringEvents.length === 0 && (
      <div className="text-fluid-sm text-gray-500">No recurring events configured.</div>
    )}
    {recurringEvents.map((re, idx) => {
      const error = recurringEventErrors[idx];
      const isYearly = re.recurrencePattern === "YEARLY";
      const rowDisabled = !canEditRecurringEvent(re);
      // Explicit colors for editable rows: in dark mode an enabled input otherwise gets a dark
      // field with the panel's near-black text. Disabled rows keep the browser's grey.
      const rowInputColors = rowDisabled ? "" : " bg-white text-gray-900";
      return (
        <div key={idx} className="border border-gray-200 rounded-lg p-fluid-2">
          <div className="flex flex-wrap items-center gap-fluid-2">
            {isDraftOnlyMode && isUnsavedRecurringEvent(re) && (
              <span className="text-xs font-bold text-green-700 bg-green-200 px-2 py-0.5 rounded">
                NEW
              </span>
            )}
            <input
              type="text"
              value={re.eventName}
              placeholder="Event name"
              onChange={(e) => updateRecurringEventField(idx, "eventName", e.target.value)}
              disabled={rowDisabled}
              className={`border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
              style={{ minWidth: "10rem" }}
            />

            <label className="inline-flex items-center gap-1 whitespace-nowrap text-fluid-sm">
              <input
                type="checkbox"
                checked={re.allDay}
                disabled={rowDisabled || isYearly}
                onChange={(e) => updateRecurringEventField(idx, "allDay", e.target.checked)}
                aria-label="Mark recurring event as all-day"
              />
              All Day
            </label>

            {!re.allDay && (
              <>
                <input
                  type="time"
                  value={re.startTime}
                  onChange={(e) => updateRecurringEventField(idx, "startTime", e.target.value)}
                  disabled={rowDisabled}
                  className={`border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
                />
                <span className="text-fluid-sm text-gray-500">to</span>
                <input
                  type="time"
                  value={re.endTime}
                  onChange={(e) => updateRecurringEventField(idx, "endTime", e.target.value)}
                  disabled={rowDisabled}
                  className={`border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
                />
              </>
            )}

            <select
              value={re.recurrencePattern}
              onChange={(e) => updateRecurringEventField(idx, "recurrencePattern", e.target.value)}
              disabled={rowDisabled}
              className="border border-gray-300 rounded px-2 py-1 text-fluid-sm"
            >
              {RECURRENCE_PATTERNS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>

            <label className="inline-flex items-center gap-1 text-fluid-sm whitespace-nowrap">
              Every
              <input
                type="number"
                min="1"
                step="1"
                value={re.intervalCount ?? ""}
                onChange={(e) => {
                  // Must tolerate an empty box mid-edit: coercing "" straight to 1
                  // refilled the field on the first backspace, so clearing it to
                  // type a new number was impossible. Normalised on blur instead.
                  const raw = e.target.value;
                  updateRecurringEventField(
                    idx,
                    "intervalCount",
                    raw === "" ? null : Number(raw)
                  );
                }}
                onBlur={() => {
                  const n = Math.floor(Number(re.intervalCount));
                  if (!Number.isFinite(n) || n < 1) {
                    updateRecurringEventField(idx, "intervalCount", 1);
                  } else if (n !== re.intervalCount) {
                    updateRecurringEventField(idx, "intervalCount", n);
                  }
                }}
                disabled={rowDisabled}
                className={`w-14 border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
              />
              {INTERVAL_UNIT_LABELS[re.recurrencePattern]}
            </label>

            {re.recurrencePattern === "WEEKLY" && (
              <DayOfWeekMultiSelect
                value={re.daysOfWeek}
                onChange={(days) => updateRecurringEventField(idx, "daysOfWeek", days)}
                disabled={rowDisabled}
              />
            )}

            {re.recurrencePattern === "MONTHLY" && (
              <>
                <select
                  value={re.monthlyMode || "DAY_OF_MONTH"}
                  onChange={(e) => updateRecurringEventField(idx, "monthlyMode", e.target.value)}
                  disabled={rowDisabled}
                  className="border border-gray-300 rounded px-2 py-1 text-fluid-sm"
                >
                  <option value="DAY_OF_MONTH">Day of month</option>
                  <option value="DAY_OF_WEEK">Day of week</option>
                </select>

                {re.monthlyMode === "DAY_OF_WEEK" ? (
                  <>
                    <select
                      value={re.weekOfMonth ?? 1}
                      onChange={(e) =>
                        updateRecurringEventField(idx, "weekOfMonth", Number(e.target.value))
                      }
                      disabled={rowDisabled}
                      className="border border-gray-300 rounded px-2 py-1 text-fluid-sm"
                    >
                      {WEEK_OF_MONTH_OPTIONS.map((w) => (
                        <option key={w.value} value={w.value}>{w.label}</option>
                      ))}
                    </select>
                    <select
                      value={re.monthlyDayOfWeek || "MONDAY"}
                      onChange={(e) => updateRecurringEventField(idx, "monthlyDayOfWeek", e.target.value)}
                      disabled={rowDisabled}
                      className="border border-gray-300 rounded px-2 py-1 text-fluid-sm"
                    >
                      {days.map((d) => (
                        <option key={d} value={d.toUpperCase()}>{d}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <label className="inline-flex items-center gap-1 text-fluid-sm whitespace-nowrap">
                    Day
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={re.dayOfMonth ?? ""}
                      onChange={(e) =>
                        updateRecurringEventField(idx, "dayOfMonth", Number(e.target.value) || null)
                      }
                      disabled={rowDisabled}
                      className={`w-16 border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
                    />
                  </label>
                )}
              </>
            )}

            {isYearly && (
              <>
                <label className="inline-flex items-center gap-1 text-fluid-sm whitespace-nowrap">
                  Month
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={re.monthOfYear ?? ""}
                    onChange={(e) =>
                      updateRecurringEventField(idx, "monthOfYear", Number(e.target.value) || null)
                    }
                    disabled={rowDisabled}
                    className={`w-16 border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
                  />
                </label>
                <label className="inline-flex items-center gap-1 text-fluid-sm whitespace-nowrap">
                  Day
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={re.dayOfMonth ?? ""}
                    onChange={(e) =>
                      updateRecurringEventField(idx, "dayOfMonth", Number(e.target.value) || null)
                    }
                    disabled={rowDisabled}
                    className={`w-16 border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
                  />
                </label>
              </>
            )}

            <label className="inline-flex items-center gap-1 text-fluid-sm whitespace-nowrap">
              Effective
              <input
                type="date"
                value={re.effectiveDate}
                onChange={(e) => updateRecurringEventField(idx, "effectiveDate", e.target.value)}
                disabled={rowDisabled}
                className={`border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
              />
            </label>

            <label className="inline-flex items-center gap-1 text-fluid-sm whitespace-nowrap">
              Ends
              <select
                value={re.endMode === "ON_DATE" ? "ON_DATE" : "INDEFINITELY"}
                onChange={(e) => updateRecurringEventField(idx, "endMode", e.target.value)}
                disabled={rowDisabled}
                className="border border-gray-300 rounded px-2 py-1 text-fluid-sm"
              >
                <option value="INDEFINITELY">Indefinitely</option>
                <option value="ON_DATE">On date</option>
              </select>
            </label>

            {re.endMode === "ON_DATE" && (
              <label className="inline-flex items-center gap-1 text-fluid-sm whitespace-nowrap">
                End date
                <input
                  type="date"
                  value={re.endDate || ""}
                  min={re.effectiveDate || undefined}
                  onChange={(e) => updateRecurringEventField(idx, "endDate", e.target.value || null)}
                  disabled={rowDisabled}
                  className={`border border-gray-300 rounded px-2 py-1 text-fluid-sm${rowInputColors}`}
                />
              </label>
            )}

            {!rowDisabled && (
              <button
                onClick={() => removeRecurringEvent(idx)}
                disabled={deletingRecurringEventId === re.recurringEventId}
                className="
                  p-fluid-1
                  rounded-md
                  text-Delete-red
                  flex items-center justify-center
                  disabled:opacity-60 disabled:cursor-not-allowed
                "
              >
                <Trash2 className="w-fluid-icon-xs h-fluid-icon-xs" strokeWidth={2.3} />
              </button>
            )}
          </div>

          {error && (
            <div className="mt-fluid-1 text-fluid-xs text-red-600">{error}</div>
          )}
        </div>
      );
    })}
  </div>

  {showRecurringDraftActions && (
    <div className="mt-fluid-2 flex flex-wrap items-center justify-between gap-fluid-2 border-t border-gray-200 pt-fluid-2">
      <p className="text-fluid-xs text-gray-600">{recurringDraftsHint}</p>
      <div className="flex items-center gap-fluid-2">
        <button
          type="button"
          onClick={discardRecurringDrafts}
          disabled={draftsBusy}
          className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={applyRecurringDrafts}
          disabled={draftsBusy}
          className={`px-4 py-2 rounded-md text-sm font-semibold transition ${
            draftsBusy
              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
              : "bg-[#1e2a74] text-white hover:bg-[#18225c]"
          }`}
        >
          {draftsBusy ? "Applying..." : recurringDraftsApplyLabel}
        </button>
      </div>
    </div>
  )}
</div>

    </section>
  );
};

export default DockDoorSettings;
